import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';

/**
 * This class helps Angular decide when to reuse a route’s component instead of creating a new one.
 * 
 * It keeps a cache of route components so that when you go back to a route,
 * the component can be reused, which can make the app faster and smoother.
 * 
 * To use this, you add `data: { reuse: true }` in your route config for the routes
 * you want to cache and reuse. Only do this for routes where reusing makes sense,
 * like pages with heavy data or lists, because caching too many routes can cause
 * problems or use too much memory.
 * 
 * The cache only keeps 10 routes at a time. If more routes are cached, the oldest one
 * gets removed. You can change this number if needed, but 10 is a good balance for most apps.
 * 
 * Overall, this helps improve app performance by avoiding unnecessary component reloads,
 * but it should be used carefully.
 */
export class CustomRouteReuseStrategy implements RouteReuseStrategy {
    /** 
     * Cache storing detached route handles by their unique route keys.
     */
    private readonly cachedRouteHandles = new Map<string, DetachedRouteHandle>();

    /** 
     * Maximum number of route components to cache.
     * When exceeded, the least recently used (LRU) cached route will be removed.
     * 
     * This is currently set to 10 but can be increased if needed,
     * depending on the app’s memory constraints and performance requirements.
     */
    private readonly maxCacheSize = 10;

    shouldDetach(route: ActivatedRouteSnapshot): boolean {
        return this.shouldReuseRouteSnapshot(route);
    }

    store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
        if (!this.shouldReuseRouteSnapshot(route)) return;

        const key = this.getRouteKey(route);

        // Update recency: delete and re-set to move to the end
        if (this.cachedRouteHandles.has(key)) {
            this.cachedRouteHandles.delete(key);
        }

        this.cachedRouteHandles.set(key, handle);

        // Enforce LRU: delete the oldest if over capacity
        if (this.cachedRouteHandles.size > this.maxCacheSize) {
            const oldestKey = this.cachedRouteHandles.keys().next().value;
            if (oldestKey !== undefined) {
                this.cachedRouteHandles.delete(oldestKey);
            }
        }

    }

    shouldAttach(route: ActivatedRouteSnapshot): boolean {
        if (!this.shouldReuseRouteSnapshot(route)) return false;
        const key = this.getRouteKey(route);
        return this.cachedRouteHandles.has(key);
    }

    retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
        if (!this.shouldReuseRouteSnapshot(route)) return null;

        const key = this.getRouteKey(route);
        const handle = this.cachedRouteHandles.get(key);

        if (handle) {
            // Update recency: move to the end (recently used)
            this.cachedRouteHandles.delete(key);
            this.cachedRouteHandles.set(key, handle);
        }

        return handle ?? null;
    }

    shouldReuseRoute(
        future: ActivatedRouteSnapshot,
        curr: ActivatedRouteSnapshot
    ): boolean {
        return future.routeConfig === curr.routeConfig;
    }

    /**
     * Determines whether the given `ActivatedRouteSnapshot` should be reused
     * based on the route configuration and the presence of the `reuse` flag
     * in the route's static data.
     *
     * To enable reuse for a specific route, add `data: { reuse: true }` in its
     * route definition.
     *
     * **Example usage in route config:**
     * ```ts
     * {
     *   path: '',
     *   pathMatch: 'full',
     *   data: { reuse: true },
     *   loadComponent: () => import('./user-list/user-list.component').then(m => m.UserListComponent),
     * }
     * ```
     *
     * @param route The route snapshot to check for reuse eligibility.
     * @returns `true` if the route is eligible for reuse; otherwise, `false`.
     *
     * @important Use the `reuse: true` flag **only** for routes that truly benefit from reuse,
     * such as data-heavy or list views. Avoid enabling reuse on all routes, as it can lead to
     * outdated views, unexpected behaviors, and memory issues due to retained component state.
     *
     */
    private shouldReuseRouteSnapshot(route: ActivatedRouteSnapshot): boolean {
        return !!route.routeConfig && !!route.data?.['reuse'];
    }

    /**
     * Generates a unique route key by traversing the `ActivatedRouteSnapshot` tree
     * from the current route to the root. All dynamic path parameters (e.g., `:id`)
     * are replaced with their actual values to ensure a unique key for each route instance.
     *
     * This is especially useful in this class to uniquely identify
     * route instances and manage their reuse behavior appropriately.
     *
     * **Example:**
     * - Route config: `/jobs/:jobId/candidates/:candidateJobId`
     * - Params: `{ jobId: '42', candidateJobId: '909' }`
     * - Result: `/jobs/42/candidates/909`
     *
     * @param route The `ActivatedRouteSnapshot` to generate the key from.
     * @returns A string representing the fully resolved route path.
     * @throws If any dynamic parameter is missing its corresponding value.
     */
    private getRouteKey(route: ActivatedRouteSnapshot): string {
        const segments: string[] = [];

        let current: ActivatedRouteSnapshot | null = route;
        while (current) {
            const configPath = current.routeConfig?.path;

            if (configPath) {
                // Replace dynamic params like ':id' with actual values
                const resolvedSegment = configPath.replace(/:(\w+)/g, (_, key) => {
                    const value = current?.params?.[key];
                    if (!value) {
                        throw new Error(
                            `Dynamic route param ":${key}" is missing a value in route: ${configPath}.`
                        );
                    }
                    return value;
                });

                segments.unshift(resolvedSegment);
            }

            current = current.parent;
        }

        return '/' + segments.filter(Boolean).join('/');
    }

    /**
     * Removes a cached route handle by its key.
     * @param key The unique route key to remove from cache.
     */
    public removeRoute(key: string): void {
        this.cachedRouteHandles.delete(key);
    }

    /**
     * Clears all cached route handles.
     */
    public clearCache(): void {
        this.cachedRouteHandles.clear();
    }
}
